import { useEffect, useRef, useSyncExternalStore } from "react";
import { aemetJobStore } from "../../model/aemet/aemetJobStore.ts";
import { AemetFetchDialog } from "./AemetFetchDialog.tsx";
import { useAemetFetch } from "./useAemetFetch.ts";
import type { ProjectSeries } from "../../model/dailySeries.ts";

/**
 * Renders the ONE AEMET fetch dialog that's currently open, if any — see
 * aemetJobStore.ts's openDialog()/closeDialog(). Deliberately independent
 * of EditSubBasinDialog.tsx: that dialog closes itself the moment it starts
 * (or reopens) an AEMET job, instead of stacking two windows on top of each
 * other, while this one keeps working through station candidates → date
 * range → download → report on its own. Mounted once, unconditionally, near
 * the top of App.tsx — Dialog.tsx portals to `document.body` regardless of
 * where this sits in the tree, so placement here doesn't matter.
 */
export function AemetFetchDialogHost({ series }: { series: ProjectSeries }) {
  const openSubBasinCode = useSyncExternalStore(
    aemetJobStore.subscribe,
    aemetJobStore.getOpenDialogSubBasinCode
  );
  const aemet = useAemetFetch({
    // A placeholder code while nothing's open — getJob() on it just returns
    // undefined, same as any other never-started job; harmless, and lets
    // this hook run unconditionally (hooks can't be called conditionally).
    subBasinCode: openSubBasinCode ?? -1,
    series,
  });

  // Same rule as EditSubBasinDialog.tsx used to apply to itself: once a
  // fetch finishes AND applies successfully AND nothing failed that could
  // still be retried, there's nothing left to show — close the dialog and
  // drop the job (which is also what clears its station markers off the
  // map, see AemetStationsLayer.tsx). A failed-to-apply result stays open
  // (shows "Retry applying" instead), and so does a successfully-applied
  // one with leftover `failedWindows` (shows the list + "Retry N failed
  // requests") — otherwise the retry button this dialog offers would never
  // actually be reachable, since apply succeeds (all-NaN days included)
  // independently of whether every window downloaded.
  const doneAndClean =
    aemet.phase === "report" &&
    !aemet.applyErrorMessage &&
    (aemet.downloadResult?.failedWindows.length ?? 0) === 0;

  // Real bug found live: AemetJobsPopover.tsx's "click a job's name to
  // reopen its dialog" also targets already-finished jobs (its own header
  // comment calls that out explicitly, for a failed-apply job's "Retry
  // applying" button) — but reopening one that finished CLEANLY used to
  // trip this same auto-close check the instant it opened, since
  // `doneAndClean` was already true on arrival: the dialog flashed open
  // and immediately vanished, with no way to actually read its coverage
  // report. `openedAlreadyDoneRef` tells the two cases apart — updated
  // during render (the standard "derive from a prop change" ref pattern,
  // not a render-phase side effect) the moment `openSubBasinCode` itself
  // changes, i.e. exactly when a dialog opens/switches sub-basin: `true`
  // means this dialog was reopened onto a job already sitting at "report",
  // clean, so auto-close must NOT fire for it; `false` means it was opened
  // while still in flight (or on a job with something to retry), so the
  // ORIGINAL behavior — auto-close the moment it finishes cleanly while
  // being watched — still applies.
  const prevOpenSubBasinCodeRef = useRef<number | null>(null);
  const openedAlreadyDoneRef = useRef(false);
  if (openSubBasinCode !== prevOpenSubBasinCodeRef.current) {
    prevOpenSubBasinCodeRef.current = openSubBasinCode;
    openedAlreadyDoneRef.current = openSubBasinCode != null && doneAndClean;
  }

  useEffect(() => {
    if (openSubBasinCode != null && doneAndClean && !openedAlreadyDoneRef.current) {
      aemet.dismiss();
      aemetJobStore.closeDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- aemet.dismiss is a fresh closure every render (not stable); including it would just re-run this same check redundantly instead of only when doneAndClean actually changes. openedAlreadyDoneRef is a ref, deliberately excluded.
  }, [openSubBasinCode, doneAndClean]);

  if (openSubBasinCode == null) return null;

  return (
    <AemetFetchDialog
      state={aemet}
      onClose={() => {
        // Only drop the tracked job (dismiss) once it's fully done, applied,
        // AND has nothing left to retry — an in-progress download, a
        // failed-to-apply result, or leftover failed windows must keep
        // going/stay available in the background; closing here only hides
        // the dialog (and, via aemetJobStore's own openDialogSubBasinCode,
        // its station markers — see AemetStationsLayer.tsx), never cancels
        // the job itself.
        if (doneAndClean) aemet.dismiss();
        aemetJobStore.closeDialog();
      }}
    />
  );
}
